/**
 * describeWbError — turns a failed Weight & Balance request into something a
 * dispatcher can act on.
 *
 * `apiRequest` throws `Error("HTTP 500: Internal Server Error")` with the raw
 * status on `err.response.status`. Printing that string at the user is a dead
 * end: it names no cause and offers no way out.
 *
 * The 5xx wording is deliberately hedged. The dominant cause is an aircraft
 * whose fuel density is missing or zero — the API divides pounds by it while
 * building the fuel table and throws on a zero divisor — but the envelope
 * endpoint does not return `fuelDensity`, so the client cannot PROVE that is
 * what happened. It says "usually", points at the setting to check, and lets
 * the user retry.
 *
 * @param {Error} err
 * @returns {{status: number|null, title: string, hint: string|null, canRetry: boolean}}
 */
export function describeWbError(err) {
	const status =
		err && err.response && typeof err.response.status === 'number' ? err.response.status : null;

	if (status === 401 || status === 403) {
		return {
			status,
			title: 'You do not have access to Weight & Balance for this event',
			hint: 'Ask an administrator to grant the Weight & Balance permission.',
			canRetry: false
		};
	}

	if (status === 404) {
		return {
			status,
			title: 'Weight & Balance is not available for this event',
			hint: 'The event may not have an aviation resource attached.',
			canRetry: false
		};
	}

	if (status !== null && status >= 500) {
		return {
			status,
			title: 'The server could not calculate Weight & Balance',
			hint:
				"This usually means the aircraft's fuel density is missing or set to zero. " +
				"Open the aircraft's Weight & Balance settings, set a fuel density, then try again.",
			canRetry: true
		};
	}

	if (status === null) {
		return {
			status,
			title: 'Could not reach the server',
			hint: 'Check your connection and try again.',
			canRetry: true
		};
	}

	return {
		status,
		title: 'Failed to load Weight & Balance',
		hint: (err && err.message) || null,
		canRetry: true
	};
}

export default describeWbError;
